//* Importing libries from React
import React, { useMemo, useCallback, useState, useEffect } from "react";

//* Importing components from react-bootstrap
import {
  Table,
  InputGroup,
  FormControl,
  Badge,
  Row,
  Col,
  Button,
} from "react-bootstrap";

//*Importing icons from lucide-react
import { Filter, Edit, AlertCircle } from "lucide-react";

//*Importing hooks from react-router
import { Link } from "react-router-dom";

//*Importing types from types
import { type Team } from "../../../hooks/units/types/types";
import { type Pagination } from "../../../services/stores/types";

//* Importar o debounce do lodash
var debounce = require("lodash.debounce");

//* types for props
type TeamsTabProps = {
  teamsData: {
    data: Team[];
    pagination: Pagination;
  };
  totalTeams: number;
  teamFilters: {
    searchTermTeams: string;
    pageTeams: number;
    perPageTeams: number;
  };
  setTeamFilters: React.Dispatch<
    React.SetStateAction<{
      searchTermTeams: string;
      pageTeams: number;
      perPageTeams: number;
    }>
  >;
  reloadData: () => void;
};

export const TeamsTab: React.FC<TeamsTabProps> = ({
  teamsData,
  totalTeams,
  teamFilters,
  setTeamFilters,
  reloadData,
}) => {
  const { data: teams, pagination } = teamsData;

  const shiftTranslations = useMemo(
    () => ({
      MORNING: "Manhã",
      AFTERNOON: "Tarde",
      NIGHT: "Noite",
      FULL: "Integral",
    }),
    []
  );

  const getShiftBadgeClass = useCallback(
    (shift: keyof typeof shiftTranslations): string => {
      const translatedShift = shiftTranslations[shift] || shift;
      switch (translatedShift) {
        case "Manhã":
          return "bg-primary text-white";
        case "Tarde":
          return "bg-info text-white";
        case "Noite":
          return "bg-dark text-white";
        default:
          return "bg-secondary text-white";
      }
    },
    [shiftTranslations]
  );

  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Data inválida";
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }, []);

  // Estado local para o valor do input de pesquisa
  const [searchTerm, setSearchTerm] = useState(teamFilters.searchTermTeams);

  // Função debounced para atualizar os filtros
  const debouncedSetFilters = useMemo(
    () =>
      debounce((value: string) => {
        setTeamFilters((prev) => ({
          ...prev,
          searchTermTeams: value,
          pageTeams: 1,
        }));
      }, 500), // 500ms de debounce
    [setTeamFilters]
  );

  // Atualiza o estado local do input e aplica o debounce nos filtros
  const handleSearchTeams = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value); // Atualiza o valor do input imediatamente
    debouncedSetFilters(value); // Aplica debounce na atualização dos filtros
  };

  // Sincroniza o estado local quando o filtro externo muda
  useEffect(() => {
    setSearchTerm(teamFilters.searchTermTeams);
  }, [teamFilters.searchTermTeams]);

  // Limpa o debounce quando o componente é desmontado
  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setTeamFilters((prev) => ({
        ...prev,
        pageTeams: newPage,
      }));
    }
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={12}>
          <InputGroup>
            <InputGroup.Text>
              <Filter size={16} />
            </InputGroup.Text>
            <FormControl
              placeholder="Pesquisar turmas..."
              value={searchTerm}
              onChange={handleSearchTeams}
              aria-label="Pesquisar turmas"
            />
          </InputGroup>
        </Col>
      </Row>

      {teams.length === 0 ? (
        <div className="text-center text-muted py-5">
          <AlertCircle size={48} className="mb-3" />
          <p>Nenhuma turma encontrada.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="table-hover align-middle">
              <thead className="bg-primary text-white">
                <tr>
                  <th>Nome</th>
                  <th>Total de Alunos</th>
                  <th>Alunos Ativos</th>
                  <th>Alunos Inativos</th>
                  <th>Turno</th>
                  <th>Início</th>
                  <th>Término</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team: Team) => {
                  const inactiveStudents =
                    (team.totalStudents || 0) - (team.totalActiveStudents || 0);
                  return (
                    <tr key={team.id}>
                      <td className="fw-bold">{team.name}</td>
                      <td>{team.totalStudents || 0}</td>
                      <td
                        className={
                          (team.totalActiveStudents || 0) > 0
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        {team.totalActiveStudents || 0}
                      </td>
                      <td
                        className={
                          inactiveStudents > 0 ? "text-danger" : "text-success"
                        }
                      >
                        {inactiveStudents}
                      </td>
                      <td>
                        <Badge
                          className={`px-2 py-1 ${getShiftBadgeClass(
                            team.shift as keyof typeof shiftTranslations
                          )}`}
                        >
                          {shiftTranslations[
                            team.shift as keyof typeof shiftTranslations
                          ] || team.shift}
                        </Badge>
                      </td>
                      <td>{formatDate(team.startDate)}</td>
                      <td>{formatDate(team.endDate)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          as={Link as any}
                          to={`/turmas/edit/${team.id}`}
                        >
                          <Edit size={16} className="me-1" />
                          Editar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Controles de Paginação */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Página {pagination.currentPage} de {pagination.totalPages}
            </div>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                className="ms-2"
              >
                Próximo
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
